import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { Store } from '@ngxs/store';
import { FieldDef } from '@core/models';
import { RelatedToType } from '@shared/enums';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { Task, TaskRelatedToModule } from '@tasks/tasks.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project } from 'app/modules/projects/projects.types';
import cloneDeep from 'lodash-es/cloneDeep';
import { TaskState } from '@tasks/state/state';

@Component({
  selector: 'app-task-details-key-date-linking',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, TsSelectModule],
  template: `
    <div class="flex flex-col gap-4">
      <div [class]="isModal ? 'flex gap-4' : 'flex flex-col gap-4'">
        <div [class]="isModal ? 'flex flex-col flex-1' : 'flex flex-col'">
          <label class="mb-2 text-sm font-medium text-gray-700">Link Type</label>
          <ts-select
            [(ngModel)]="selectedLinkType"
            [items]="linkTypes"
            itemKey="value"
            itemLabel="label"
            [disabled]="!isEditable"
            (ngModelChange)="onValueChange()"
            data-testid="task-link-type-select"
            [uiOptions]="{
              roundedBorders: false,
              size: 'medium',
              placeholder: 'Select a type',
              disabledClass: 'disabled-transparent',
            }"
          ></ts-select>
        </div>
        <div [class]="isModal ? 'flex flex-col flex-1' : 'flex flex-col'">
          <label class="mb-2 text-sm font-medium text-gray-700">Deal Field</label>
          <ts-select
            [(ngModel)]="selectedFieldName"
            [items]="dateFields"
            itemKey="name"
            itemLabel="label"
            [disabled]="!isEditable"
            (ngModelChange)="onValueChange()"
            data-testid="task-deal-field-select"
            [uiOptions]="{
              roundedBorders: false,
              size: 'medium',
              placeholder: 'Select a field',
              disabledClass: 'disabled-transparent',
            }"
          ></ts-select>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskDetailsKeyDateLinkingComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private projectsService = inject(ProjectsService);
  private cdr = inject(ChangeDetectorRef);

  selectedLinkType = '';
  selectedFieldName = '';

  private _task: Task;
  @Input() set task(value: Task) {
    this._task = cloneDeep(value);
    this.updateValues();
  }
  get task(): Task {
    return this._task;
  }

  @Input() isModal = false;
  @Output() updateTask = new EventEmitter<Task>();

  dateFields: FieldDef[] = [];
  currentProject: Project;
  isEditable = false;
  linkTypes = [
    { value: '', label: 'None' },
    { value: 'linked_key_date_field_as_target', label: 'Receive from field' },
    { value: 'linked_key_date_field_as_source', label: 'Send to field' },
  ];

  ngOnInit(): void {
    this.loadDateFields();
    this.setupProjectSubscription();
  }

  ngAfterViewInit(): void {
    this.updateValues();
  }

  private updateValues(): void {
    if (this.task?.id) {
      this.selectedFieldName =
        this.task.linked_key_date_field_as_source || this.task.linked_key_date_field_as_target || '';
      this.selectedLinkType = this.task.linked_key_date_field_as_source
        ? 'linked_key_date_field_as_source'
        : this.task.linked_key_date_field_as_target
          ? 'linked_key_date_field_as_target'
          : '';
    }
    this.cdr.detectChanges();
  }

  onValueChange(): void {
    if (!this.isEditable) {
      return;
    }

    const updatedTask = { ...this.task };

    // Reset both fields first
    updatedTask.linked_key_date_field_as_source = null;
    updatedTask.linked_key_date_field_as_target = null;

    if (this.selectedLinkType === '') {
      this.selectedFieldName = '';
    }

    if (this.selectedLinkType === 'linked_key_date_field_as_source') {
      updatedTask.linked_key_date_field_as_source = cloneDeep(this.selectedFieldName);
    } else if (this.selectedLinkType === 'linked_key_date_field_as_target') {
      updatedTask.linked_key_date_field_as_target = cloneDeep(this.selectedFieldName);
    }

    // Only emit if both fields are empty or if we have both a field name and link type
    if (
      (this.selectedFieldName === '' && this.selectedLinkType === '') ||
      (this.selectedFieldName !== '' && this.selectedLinkType !== '')
    ) {
      if (!this._task?.title || this._task.title === '') {
        const selectedDateField = this.dateFields.find((f) => f.name === this.selectedFieldName);
        updatedTask['title'] = selectedDateField?.label;
      }
      this.updateTask.emit(updatedTask);
    }
  }

  private setupProjectSubscription(): void {
    const relatedTo = this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType;
    if (relatedTo !== TaskRelatedToModule.Project) {
      this.isEditable = true;
      return;
    }

    this.projectsService.project$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project) => {
      if (project) {
        this.currentProject = project;
        this.isEditable = project.permissions?.is_editable ?? false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadDateFields(): void {
    this.store
      .select(
        ObjectsManagerState.getFieldsByObjectName(RelatedToType.Project, {
          addNestedFields: false,
          allowedFieldsOnly: true,
        }),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fields: FieldDef[]) => {
        if (fields) {
          this.dateFields = [
            { name: '', label: 'None' } as any,
            ...fields.filter(
              (field) =>
                (field.type === 'date' || field.type === 'datetime') && !field.archived && !field.meta.readonly,
            ),
          ];
          this.cdr.detectChanges();
        }
      });
  }
}
